// @ts-nocheck
/**
 * Admin Dashboard Metrics Component
 * Carbon-compliant metrics tiles for administrative dashboard
 */

import React from 'react';
import {
  Tile,
  Grid,
  Column,
  Heading,
  Stack,
  Tag,
  SkeletonPlaceholder,
  InlineLoading,
  StatusIndicator,
  ProgressBar,
  UnorderedList,
  ListItem,
} from '@carbon/react';
import {
  User,
  UserMultiple,
  Education,
  ChartLine,
  TestTool,
  View,
  Checkmark,
  Warning,
  Error as ErrorIcon,
  Restart,
} from '@carbon/icons-react';

interface PlatformStats {
  total_users: number;
  total_students: number;
  total_parents: number;
  total_teachers: number;
  total_admins: number;
  total_families: number;
  active_sessions: number;
}

interface MarketingMetrics {
  conversionRate: number;
  activeTests: number;
  cacheHitRate: number;
  contentItems: number;
}

interface SystemHealth {
  database: string;
  api: string;
  aiServices: string;
  storage: string;
}

interface DashboardMetricsProps {
  stats: PlatformStats | null;
  marketingMetrics: MarketingMetrics;
  systemHealth: SystemHealth;
  loading?: boolean;
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({
  stats,
  marketingMetrics,
  systemHealth,
  loading = false,
}) => {
  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <Checkmark style={{ color: 'var(--cds-support-success)' }} size={20} />;
      case 'degraded':
        return <Warning style={{ color: 'var(--cds-support-warning)' }} size={20} />;
      case 'down':
        return <ErrorIcon style={{ color: 'var(--cds-support-error)' }} size={20} />;
      default:
        return <Restart style={{ color: 'var(--cds-support-info)' }} size={20} />;
    }
  };

  const getHealthTagType = (status: string): 'green' | 'red' | 'gray' | 'blue' => {
    switch (status) {
      case 'healthy':
        return 'green';
      case 'down':
        return 'red';
      case 'degraded':
        return 'red';
      case 'checking':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const renderHealthStatus = (status: string, label: string) => {
    if (status === 'checking') {
      return (
        <Stack gap={3}>
          <InlineLoading status="active" description={`Checking ${label.toLowerCase()} status...`} />
          <p
            style={{
              fontWeight: 'var(--cds-body-01-font-weight)',
              fontSize: 'var(--cds-body-01-font-size)',
            }}
          >
            {label}
          </p>
          <Tag type="blue">Checking...</Tag>
        </Stack>
      );
    }

    return (
      <Stack gap={3}>
        {getHealthIcon(status)}
        <p
          style={{
            fontWeight: 'var(--cds-body-01-font-weight)',
            fontSize: 'var(--cds-body-01-font-size)',
          }}
        >
          {label}
        </p>
        <Tag type={getHealthTagType(status)}>{status.charAt(0).toUpperCase() + status.slice(1)}</Tag>
      </Stack>
    );
  };

  if (loading) {
    return (
      <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
        <Grid narrow>
          {Array.from({ length: 8 }).map((_, index) => (
            <Column key={index} sm={2} md={2} lg={2}>
              <Tile>
                <SkeletonPlaceholder style={{ height: '120px', width: '100%' }} />
              </Tile>
            </Column>
          ))}
        </Grid>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
      {/* Platform Statistics */}
      <div style={{ marginBottom: 'var(--cds-spacing-06)' }}>
        <Heading
          size="md"
          style={{
            marginBottom: 'var(--cds-spacing-05)',
            fontFamily: 'var(--cds-productive-heading-03-font-family)',
            fontSize: 'var(--cds-productive-heading-03-font-size)',
            fontWeight: 'var(--cds-productive-heading-03-font-weight)',
            lineHeight: 'var(--cds-productive-heading-03-line-height)',
          }}
        >
          Platform Statistics
        </Heading>
        <Grid narrow>
          <Column sm={2} md={2} lg={2}>
            <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
              <Stack gap={3}>
                <UserMultiple size={24} style={{ color: 'var(--cds-icon-primary)' }} />
                <Heading
                  size="lg"
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                  }}
                >
                  {stats?.total_users || 0}
                </Heading>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-body-01-font-size)',
                  }}
                >
                  Total Users
                </p>
              </Stack>
            </Tile>
          </Column>
          <Column sm={2} md={2} lg={2}>
            <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
              <Stack gap={3}>
                <User size={24} style={{ color: 'var(--cds-icon-primary)' }} />
                <Heading
                  size="lg"
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                  }}
                >
                  {stats?.total_students || 0}
                </Heading>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-body-01-font-size)',
                  }}
                >
                  Students
                </p>
              </Stack>
            </Tile>
          </Column>
          <Column sm={2} md={2} lg={2}>
            <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
              <Stack gap={3}>
                <Education size={24} style={{ color: 'var(--cds-icon-primary)' }} />
                <Heading
                  size="lg"
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                  }}
                >
                  {stats?.total_teachers || 0}
                </Heading>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-body-01-font-size)',
                  }}
                >
                  Teachers
                </p>
              </Stack>
            </Tile>
          </Column>
          <Column sm={2} md={2} lg={2}>
            <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
              <Stack gap={3}>
                <UserMultiple size={24} style={{ color: 'var(--cds-icon-primary)' }} />
                <Heading
                  size="lg"
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                  }}
                >
                  {stats?.total_parents || 0}
                </Heading>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-body-01-font-size)',
                  }}
                >
                  Parents
                </p>
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>

      {/* Marketing Metrics Summary */}
      <div style={{ marginBottom: 'var(--cds-spacing-06)' }}>
        <Heading
          size="md"
          style={{
            marginBottom: 'var(--cds-spacing-05)',
            fontFamily: 'var(--cds-productive-heading-03-font-family)',
            fontSize: 'var(--cds-productive-heading-03-font-size)',
            fontWeight: 'var(--cds-productive-heading-03-font-weight)',
            lineHeight: 'var(--cds-productive-heading-03-line-height)',
          }}
        >
          Marketing Performance Overview
        </Heading>
        <Grid narrow>
          <Column sm={2} md={2} lg={2}>
            <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
              <Stack gap={3}>
                <ChartLine size={24} style={{ color: 'var(--cds-support-success)' }} />
                <Heading
                  size="lg"
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                  }}
                >
                  {marketingMetrics.conversionRate}%
                </Heading>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-body-01-font-size)',
                  }}
                >
                  Conversion Rate
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-02)' }}>
                  <p
                    style={{
                      color: 'var(--cds-support-success)',
                      fontSize: 'var(--cds-helper-text-01-font-size)',
                    }}
                  >
                    +2.3% this month
                  </p>
                </div>
                <ProgressBar
                  value={marketingMetrics.conversionRate}
                  max={20}
                  size="small"
                  label="Conversion rate progress"
                  hideLabel
                  helperText={`${marketingMetrics.conversionRate}% of target`}
                />
              </Stack>
            </Tile>
          </Column>
          <Column sm={2} md={2} lg={2}>
            <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
              <Stack gap={3}>
                <TestTool size={24} style={{ color: 'var(--cds-interactive-01)' }} />
                <Heading
                  size="lg"
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                  }}
                >
                  {marketingMetrics.activeTests}
                </Heading>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-body-01-font-size)',
                  }}
                >
                  Active A/B Tests
                </p>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-helper-text-01-font-size)',
                  }}
                >
                  2 pending results
                </p>
              </Stack>
            </Tile>
          </Column>
          <Column sm={2} md={2} lg={2}>
            <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
              <Stack gap={3}>
                <ChartLine size={24} style={{ color: 'var(--cds-support-info)' }} />
                <Heading
                  size="lg"
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                  }}
                >
                  {marketingMetrics.cacheHitRate}%
                </Heading>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-body-01-font-size)',
                  }}
                >
                  Cache Hit Rate
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-02)' }}>
                  <p
                    style={{
                      color: 'var(--cds-support-success)',
                      fontSize: 'var(--cds-helper-text-01-font-size)',
                    }}
                  >
                    Excellent performance
                  </p>
                </div>
                <ProgressBar
                  value={marketingMetrics.cacheHitRate}
                  max={100}
                  size="small"
                  label="Cache hit rate progress"
                  hideLabel
                  helperText={`${marketingMetrics.cacheHitRate}% hit rate`}
                />
              </Stack>
            </Tile>
          </Column>
          <Column sm={2} md={2} lg={2}>
            <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
              <Stack gap={3}>
                <View size={24} style={{ color: 'var(--cds-support-warning)' }} />
                <Heading
                  size="lg"
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                  }}
                >
                  {marketingMetrics.contentItems}
                </Heading>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-body-01-font-size)',
                  }}
                >
                  Content Items
                </p>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-helper-text-01-font-size)',
                  }}
                >
                  12 pending approval
                </p>
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>

      {/* System Health */}
      <div>
        <Heading
          size="md"
          style={{
            marginBottom: 'var(--cds-spacing-05)',
            fontFamily: 'var(--cds-productive-heading-03-font-family)',
            fontSize: 'var(--cds-productive-heading-03-font-size)',
            fontWeight: 'var(--cds-productive-heading-03-font-weight)',
            lineHeight: 'var(--cds-productive-heading-03-line-height)',
          }}
        >
          System Health
        </Heading>
        <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
          <Grid narrow>
            <Column sm={1} md={1} lg={1}>
              {renderHealthStatus(systemHealth.database, 'Database')}
            </Column>
            <Column sm={1} md={1} lg={1}>
              {renderHealthStatus(systemHealth.api, 'API Services')}
            </Column>
            <Column sm={1} md={1} lg={1}>
              {renderHealthStatus(systemHealth.aiServices, 'AI Services')}
            </Column>
            <Column sm={1} md={1} lg={1}>
              {renderHealthStatus(systemHealth.storage, 'Storage')}
            </Column>
          </Grid>
        </Tile>
      </div>
    </div>
  );
};

export default DashboardMetrics;
